import { FC, HTMLAttributes, ReactNode } from "react";
import Image from "next/image";
import Link from "next/link";
import clsx from "clsx";

import Logo from "@/assets/images/branding/logo-white.svg";

import HtmlText from "../common/HtmlText";
import TButton from "@/components/atoms/buttons/TButton";

import { ITextButton } from "@/components/atoms/buttons/types";
import CourseOverviewCard, {
  ICourseOverviewCard,
} from "@/components/cards/CourseOverviewCard";

// ----------------------------------------------------------------------------------------------------------

export type ICourseOverviewSectionProps = HTMLAttributes<HTMLElement> & {
  heading?: string;
  description?: ReactNode;
  button?: ITextButton;
  cards?: ICourseOverviewCard[];
  video?: string;
  videoLink?: string;
};

const CourseOverviewSection: FC<ICourseOverviewSectionProps> = (props) => {
  const {
    heading,
    description,
    button,
    cards,
    video,
    videoLink,
    className,
    ...other
  } = props;

  return (
    <section
      className={clsx(
        "pb-5 pt-20 dark:bg-black-900 lg:pb-8 lg:pt-28 xl:pb-[50px] xl:pt-[150px]",
        className,
      )}
      {...other}
    >
      <div className="container mx-auto">
        {/* Logo */}
        <div>
          <Link href="/">
            <Image
              src={Logo.src}
              alt="Certbar logo"
              width={276}
              height={63}
              className="h-14 w-auto md:h-[63px]"
            />
          </Link>
        </div>

        {/* title and description */}
        <div className="mt-7 lg:mt-12">
          <h1 className="mb-3 text-3xl font-semibold dark:text-yellow-400 lg:mb-5 lg:text-h2 xl:mb-7 xl:text-h1">
            {heading}
          </h1>
          <HtmlText>{description}</HtmlText>
        </div>

        {/* Two box  */}
        {cards && cards.length > 0 && (
          <div className="mt-5 grid grid-cols-12 gap-5 md:gap-7 lg:mt-7">
            {cards.map((item, idx) => (
              <CourseOverviewCard
                key={idx}
                heading={item.heading}
                subHeading={item.subHeading}
                description={item.description}
              />
            ))}
          </div>
        )}

        {/* Button */}
        {button && (
          <div className="mt-5 flex flex-row flex-wrap gap-4 md:gap-8 lg:mt-7 lg:flex-col">
            <TButton
              id={button.id}
              href={button.link}
              target={button.target}
              radius={button.radius}
              variant={button.variant}
            >
              {button.text}
            </TButton>
          </div>
        )}
      </div>
    </section>
  );
};

export default CourseOverviewSection;
